import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store";
import { logout, selectIsAuthenticated } from "../store/slices/authSlice";
import { Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { useLazyStaffStoreAppDetailsQuery } from "../api";
import { getAppKey } from "../shared/lib/helpers";
import { convertServicesToEnvironment } from "../shared/lib/environment";
import { updateGlobalAPIBaseURLs } from "../api/axios/api-instances";
import { refreshAccessToken } from "../api/helpers/refresh-token";
import { ExitIcon } from "@radix-ui/react-icons";

export function ProtectedView() {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const { storeKey } = useParams();

  const initializeEnvironment = useCallback(
    async (storeKey: string) => {
      const appDetails = await getAppDetails({
        store_key: storeKey,
        app_key: getAppKey(),
      }).unwrap();

      if (appDetails) {
        await refreshAccessToken();

        const services = convertServicesToEnvironment(appDetails.result.services);
        updateGlobalAPIBaseURLs(services);
        setIsLoading(false);
      }
    },
    [getAppDetails]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      setIsLoading(false);
    } else {
      if (storeKey) {
        Promise.all([initializeEnvironment(storeKey)]).catch((err) => {
          setError(err);
          setIsLoading(false);
        });
      } else {
        navigate("/stores");
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, initializeEnvironment, navigate, storeKey]);

  return (
    <>
      {isLoading ? (
        <Flex className="h-dvh" gap="2" justify="center" align="center">
          <Spinner size="3" />
          <Text color="gray" size="2">
            Starting app...
          </Text>
        </Flex>
      ) : error ? (
        <ErrorView error={error} />
      ) : (
        <Outlet />
      )}
    </>
  );
}

function ErrorView({ error }: { error: any }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  return (
    <Flex className="h-dvh" gap="1" justify="center" align="center">
      {error.status && (
        <Text color="gray" size="2">
          {error.status}
        </Text>
      )}
      <Text color="gray" size="2">
        {error.data.detail || "Unexpected error. Please try again."}
      </Text>

      <Button
        disabled={loading}
        className="!absolute !top-6 !right-8"
        variant="ghost"
        color="gray"
        onClick={() => {
          setLoading(true);
          dispatch(logout())
            .unwrap()
            .then(() => {
              setLoading(false);
              navigate("/login");
            });
        }}
      >
        <ExitIcon /> Logout
      </Button>
    </Flex>
  );
}
